import React, { useCallback, useContext } from 'react';
import * as http from 'utils/http';
import config from 'config';
import useSWR from 'swr';
import serialize from 'utils/serialize';

import { getBusinessFilter } from 'utils/filters';

import {
  getRouteFilter,
  groupItinerariesByDate,
  mapItinerariesForDispatchingCommon,
  mergeItinerariesAndOfferStatus,
} from 'utils/itineraries';
import { LH_EXPRESS_ROUTE_FOR_FILTER, LH_EXPRESS_FR_ROUTE_FOR_FILTER } from 'utils/lhexpress';
import { hasInternationalRouteSelected } from 'utils/international';

const fetcher = (url) => http.get(`${config.API_URL}${url}`);
const post = (url, params) => http.post(`${config.API_URL}${url}`, params);
const put = (url, params) => http.put(`${config.API_URL}${url}`, params);

const DispatchingServiceContext = React.createContext();

const _getUrlWithFilters = (url, filters = {}) => {
  const { date, route, vehicle_type, need_manual_dispatching } = filters;
  const dateFrom = date?.from;
  const dateTo = date?.to;
  const isInternational = hasInternationalRouteSelected(route);
  const isLhEs = (route || []).some((r) => r.value === LH_EXPRESS_ROUTE_FOR_FILTER.name);
  const isLhFr = (route || []).some((r) => r.value === LH_EXPRESS_FR_ROUTE_FOR_FILTER.name);
  const regularRoutes = (route || []).filter(
    (r) =>
      ![LH_EXPRESS_ROUTE_FOR_FILTER.name, LH_EXPRESS_FR_ROUTE_FOR_FILTER.name].includes(r.value)
  );

  const routeForParams = isInternational ? undefined : getRouteFilter(regularRoutes) || undefined;

  const requestParams = {
    date_from: dateFrom,
    date_to: dateTo,
    route: routeForParams,
    vehicle_type: vehicle_type
      ?.map((vehicleTypeFilter) => `${vehicleTypeFilter.value}`.toUpperCase())
      .join(','),
    need_manual_dispatching,
    page_size: 0,
    international: isInternational || undefined,
    long_haul: isLhEs || undefined,
    long_haul_fr: isLhFr || undefined,
    ...getBusinessFilter(filters?.business, 'has_ftl_shipper_order'),
  };

  return `${url}/?${serialize(requestParams)}`;
};

const useDispatchingFiltered = (swrOptions) => {
  const { filters } = useContext(DispatchingServiceContext);

  const url = _getUrlWithFilters('itineraries', filters);
  const offerStatusURL = _getUrlWithFilters('itineraries/offer_status', filters);

  const { data: itineraries, mutate: mutateItinerariesList } = useSWR(url, fetcher, swrOptions);
  const { data: offerStatus, mutate: mutateOfferStatus } = useSWR(offerStatusURL, fetcher, {
    ...(swrOptions || {}),
    refreshInterval: 10000, // ten seconds
    refreshWhenHidden: false,
    refreshWhenOffline: false,
  });

  const itinerariesWithOfferStatus = mergeItinerariesAndOfferStatus(itineraries, offerStatus);

  const shipperOrderIds = itinerariesWithOfferStatus.reduce(
    (acc, itinerary) => [...acc, ...(itinerary.shipper_order_ids || [])],
    []
  );

  const mutate = useCallback(async () => {
    await mutateItinerariesList();
    await mutateOfferStatus();
  }, [mutateItinerariesList, mutateOfferStatus]);

  return {
    itineraries,
    groupedItineraries: groupItinerariesByDate(
      mapItinerariesForDispatchingCommon(itinerariesWithOfferStatus)
    ),
    shipperOrderIds,
    mutate,
  };
};

const useDispatchingSuggestions = (shipperOrderIds = [], swrOptions) => {
  const filters = serialize({
    shipper_order_ids: shipperOrderIds.join(','),
    need_manual_dispatching: true,
    is_visible_for_agents: false,
  });

  const { data: suggestions, mutate: mutateSuggestions } = useSWR(
    shipperOrderIds.length && `itineraries/suggestions/?${filters}`,
    fetcher,
    swrOptions
  );

  return {
    suggestions,
    mutateSuggestions,
  };
};

const useDispatchingCounterFiltered = (swrOptions) => {
  const { filters } = useContext(DispatchingServiceContext);

  const url = _getUrlWithFilters('itineraries/count', filters);

  const { data: count } = useSWR(url, fetcher, swrOptions);

  const countData = count;
  const countTotal = Object.keys(count || {}).reduce((acc, date) => acc + countData[date], 0);

  return { countData, countTotal };
};

const useItineraryDetail = (id, swrOptions) => {
  const { data: itinerary } = useSWR(!!id && `itineraries/${id}/`, fetcher, swrOptions);

  return { itinerary };
};

const useOffersInfo = (itineraryId, swrOptions) => {
  const { data, error } = useSWR(
    !!itineraryId && `mercury/offers/${itineraryId}/itinerary_info/`,
    fetcher,
    swrOptions
  );

  return { data, error };
};

const useDispatchingFilters = () => {
  const { filters } = useContext(DispatchingServiceContext);

  return { filters };
};

const DispatchingServiceContextProvider = ({ children, filters }) => {
  return (
    <DispatchingServiceContext.Provider value={{ filters }}>
      {children}
    </DispatchingServiceContext.Provider>
  );
};

const getOrCreateJourneyForItinerary = async (itineraryId) => {
  return post(`itineraries/${itineraryId}/journey/`);
};

const moveItineraryToAuto = async (itineraryId) => {
  return post(`itineraries/${itineraryId}/mark-as-automatic/`);
};

const launchItinerary = async (itineraryId) => {
  return put(`itineraries/${itineraryId}/launch/`);
};
const launchToCustomPool = async ({ itineraryId, drivers }) => {
  return put(`itineraries/${itineraryId}/launch/`, {
    target_drivers: drivers,
  });
};

const breakItinerary = async (itineraryId) => {
  return put(`itineraries/${itineraryId}/break/`);
};

const moveItineraryToManual = async (itineraryId, { unlock_reason, unlock_comment } = {}) => {
  return post(`itineraries/${itineraryId}/mark-as-manual/`, {
    unlock_reason: unlock_reason,
    unlock_comment: unlock_comment,
  });
};

const assignItineraryToAgent = async (itineraryId, userId) => {
  try {
    return await put(`itineraries/${itineraryId}/claim/`, { owner_id: userId });
  } catch (err) {
    console.error(http.getError(err));
  }
};

const unassignItineraryFromAgent = async (itineraryId, userId) => {
  try {
    return await put(`itineraries/${itineraryId}/release/`, { owner_id: userId });
  } catch (err) {
    console.error(http.getError(err));
  }
};

const assignItinerary = async ({
  itineraryId,
  driverId,
  vehicleIds,
  assignmentReason,
  prebookingId = null,
}) => {
  try {
    const journey = await getOrCreateJourneyForItinerary(itineraryId);
    const journeyId = journey?.id;
    const assignedJourney = await put(`mercury/journeys/${journeyId}/assign/`, {
      driver_id: driverId,
      vehicle: null,
      vehicle_ids: vehicleIds,
      prebooking_accepted_slot_id: prebookingId,
    });

    if (assignmentReason) {
      const newJourney = await fetcher(`mercury/journeys/${journeyId}/`);
      await post(`mercury/journeys/${journeyId}/logs/`, {
        action: 'ASSIGN_DRIVER',
        reason: 'REASON_OTHER',
        comment: assignmentReason,
        changes: {
          carrier_name_after: newJourney?.carrier?.name,
          driver_name_after: newJourney?.driver?.contact_name,
          vehicle_plate_number_after: newJourney?.vehicle?.plate_number,
          trailer_plate_number_after: newJourney?.trailer?.plate_number,
        },
      });
    }

    return assignedJourney;
  } catch (err) {
    console.log('Error assignItinerary', http.getError(err));
  }
};

const changeItineraryCost = async (itineraryId, { costAdjustment } = {}) => {
  try {
    return await put(`itineraries/${itineraryId}/cost/`, { cost_adjustment: costAdjustment });
  } catch (err) {
    console.error(http.getError(err));
  }
};

const sendNoDriverWarningEmail = async ({ itineraryId }) => {
  const journey = await getOrCreateJourneyForItinerary(itineraryId);
  const journeyId = journey?.id;
  if (!journeyId) {
    return;
  }

  return await post(`mercury/journeys/${journeyId}/notify_not_assigned/`);
};

export {
  useDispatchingFiltered,
  useDispatchingCounterFiltered,
  useItineraryDetail,
  useDispatchingFilters,
  moveItineraryToAuto,
  moveItineraryToManual,
  useOffersInfo,
  assignItinerary,
  DispatchingServiceContext,
  DispatchingServiceContextProvider,
  useDispatchingSuggestions,
  getOrCreateJourneyForItinerary,
  launchItinerary,
  launchToCustomPool,
  breakItinerary,
  changeItineraryCost,
  sendNoDriverWarningEmail,
  assignItineraryToAgent,
  unassignItineraryFromAgent,
  _getUrlWithFilters,
};
